import { useEffect, useMemo, useState } from 'react';
import { ApiError, useRoute, useSession } from '@huaca/ui';
import { createTemplate, getTemplate, updateTemplate } from '../api';
import { newId } from '../ids';
import { validateConditionRefs } from '@huaca/claro-engine';
import type { TemplateBody } from '../types';
import { Skeleton } from '../Skeleton';
import { FormBuilderBody } from './FormBuilderBody';

// The reorder helper lives with the editing engine now; re-exported so builder-rules.test — which
// pins the drag-reorder at this seam (dnd-kit can't be driven in jsdom) — keeps importing it here.
export { reorder } from './FormBuilderBody';

/**
 * Master-template CRUD: the Editor-gated wrapper around the shared FormBuilderBody. It owns loading a
 * template (or an empty one) and the create/update save that navigates back to the dashboard. The
 * editing surface itself — sections, fields, conditions, reorder, preview — is FormBuilderBody, shared
 * verbatim with the per-instance SnapshotEditor. (The firm logo is firm-wide branding and lives on the
 * organization page, not here.)
 */

const emptyTemplate = (): TemplateBody => ({
  name: '',
  sections: [{ id: newId('sec'), title: 'מקטע 1', order: 0, visibleWhen: null, fields: [] }],
});

export function TemplateBuilder({ templateId }: { templateId: string | null }) {
  const { client, can } = useSession();
  const { navigate } = useRoute();
  const mayEdit = can('claro:template:edit');

  const [body, setBody] = useState<TemplateBody | null>(templateId ? null : emptyTemplate());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!templateId) return;
    let live = true;
    getTemplate(client, templateId)
      .then((t) => live && setBody({ name: t.name, sections: t.sections }))
      .catch(() => live && setLoadError('לא ניתן לטעון את התבנית.'));
    return () => {
      live = false;
    };
  }, [client, templateId]);

  const refErrors = useMemo(() => (body ? validateConditionRefs(body) : []), [body]);

  if (!mayEdit) {
    return (
      <div className="huaca-state" data-testid="builder-forbidden">
        <h2>אין לכם הרשאת עריכה</h2>
        <p>רק עורך בארגון יכול לבנות ולערוך תבניות.</p>
        <button type="button" className="btn-primary" onClick={() => navigate('/')}>
          חזרה
        </button>
      </div>
    );
  }
  if (loadError) return <p role="alert">{loadError}</p>;
  if (!body) return <Skeleton title lines={4} />;

  async function save() {
    if (!body) return;
    setSaveError(null);
    if (body.name.trim() === '') return setSaveError('יש לתת שם לתבנית.');
    if (refErrors.length > 0) return setSaveError('יש תנאי שמפנה לשדה שאינו מוגדר לפניו. תקנו אותו לפני שמירה.');
    setSaving(true);
    try {
      if (templateId) await updateTemplate(client, templateId, body);
      else await createTemplate(client, body);
      navigate('/');
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'השמירה נכשלה. נסו שוב.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="claro-builder claro-page" data-testid="template-builder">
      <div className="claro-page-head">
        <h1>{templateId ? 'עריכת תבנית' : 'תבנית חדשה'}</h1>
        <div className="claro-head-actions">
          <button type="button" onClick={() => navigate('/')}>
            ביטול
          </button>
          <button type="button" className="btn-primary" disabled={saving} onClick={() => void save()} data-testid="save-template">
            {saving ? 'שומר…' : 'שמירה'}
          </button>
        </div>
      </div>

      {saveError && <p role="alert">{saveError}</p>}

      <div className="huaca-field">
        <label htmlFor="tpl-name">שם התבנית</label>
        <input id="tpl-name" value={body.name} onChange={(e) => setBody({ ...body, name: e.target.value })} placeholder="למשל: קליטת לקוח חדש" />
      </div>

      <FormBuilderBody body={body} onChange={setBody} />
    </div>
  );
}
